/**
 * Admin Geo Modal — Editor de configurações do modal de verificação regional
 * 100% editável: etapas, textos, mapeamento país→locale, delays, cookie
 */

import { T } from '../styles/tokens';
import { useState, useEffect } from 'preact/hooks';
import { api } from '../api/client';

interface Step {
  icon: string;
  text: string;
  duration: number;
}

interface CountryMapping {
  code: string;
  locale: string;
}

// Mapeamento country code → bandeira emoji
function getFlag(code: string): string {
  if (code.length !== 2) return '🌍';
  return String.fromCodePoint(...[...code.toUpperCase()].map(c => 0x1F1E6 + c.charCodeAt(0) - 65));
}

// Mapeamento country code → nome em português
const COUNTRY_NAMES: Record<string, string> = {
  BR: 'Brasil', PT: 'Portugal', AO: 'Angola', MZ: 'Moçambique',
  US: 'Estados Unidos', GB: 'Reino Unido', AU: 'Austrália', CA: 'Canadá',
  NZ: 'Nova Zelândia', IE: 'Irlanda', ES: 'Espanha', MX: 'México',
  AR: 'Argentina', CO: 'Colômbia', CL: 'Chile', PE: 'Peru',
  FR: 'França', BE: 'Bélgica', CH: 'Suíça', DE: 'Alemanha', AT: 'Áustria',
};

const LOCALES = ['pt-BR', 'en-US', 'es-ES', 'fr-FR', 'de-DE'];

const sectionStyle: any = {
  background: T.bg2 || 'rgba(255,255,255,0.03)',
  border: `1px solid ${T.border}`,
  borderRadius: 12,
  padding: '20px 24px',
  marginBottom: 20,
};

const labelStyle: any = {
  display: 'block',
  color: T.textSecondary,
  fontSize: 13,
  fontWeight: 500,
  marginBottom: 6,
};

const inputStyle: any = {
  width: '100%',
  padding: '10px 14px',
  background: T.bg || '#0a0e1a',
  color: T.text,
  border: `1px solid ${T.border}`,
  borderRadius: 8,
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
};

const btnPrimary: any = {
  padding: '10px 24px',
  background: 'linear-gradient(135deg, #2F6BFF, #7C3AED)',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

const btnSmall: any = {
  padding: '4px 10px',
  background: 'transparent',
  color: T.textSecondary,
  border: `1px solid ${T.border}`,
  borderRadius: 6,
  fontSize: 12,
  cursor: 'pointer',
};

export function AdminGeoModal() {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [dataLoaded, setDataLoaded] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [steps, setSteps] = useState<Step[]>([]);
  const [successText, setSuccessText] = useState('');
  const [scarcityText, setScarcityText] = useState('');
  const [scarcitySlots, setScarcitySlots] = useState('12');
  const [redirectDelay, setRedirectDelay] = useState('3000');
  const [ctaText, setCtaText] = useState('');
  const [skipText, setSkipText] = useState('');
  const [cookieDays, setCookieDays] = useState('30');
  const [fallbackLocale, setFallbackLocale] = useState('en-US');
  const [countryMap, setCountryMap] = useState<CountryMapping[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  // Timing & Conversão
  const [transitionDelay, setTransitionDelay] = useState('600');
  const [successDelay, setSuccessDelay] = useState('800');
  const [progressEasing, setProgressEasing] = useState('ease-out');
  const [fadeInDuration, setFadeInDuration] = useState('300');
  const [stepAnimation, setStepAnimation] = useState('slide-up');
  const [successAnimation, setSuccessAnimation] = useState('scale-bounce');

  useEffect(() => {
    loadSettings();
  }, []);

  async function loadSettings() {
    try {
      const res = await api.get<Record<string, string>>('/admin/geo-modal/settings');
      const data = res || {};
      console.log('[GeoModal Admin] Dados carregados:', data);
      if (data.geo_modal_enabled !== undefined) setEnabled(data.geo_modal_enabled !== 'false');
      try { setSteps(JSON.parse(data.geo_modal_steps || '[]')); } catch { setSteps([]); }
      setSuccessText(data.geo_modal_success_text || '');
      setScarcityText(data.geo_modal_scarcity_text || '');
      setScarcitySlots(data.geo_modal_scarcity_slots || '12');
      setRedirectDelay(data.geo_modal_redirect_delay || '3000');
      setCtaText(data.geo_modal_cta_text || 'Acessar agora');
      setSkipText(data.geo_modal_skip_text || 'Pular');
      setCookieDays(data.geo_modal_cookie_days || '30');
      setFallbackLocale(data.geo_modal_fallback_locale || 'en-US');
      // Timing
      setTransitionDelay(data.geo_modal_transition_delay || '600');
      setSuccessDelay(data.geo_modal_success_delay || '800');
      setProgressEasing(data.geo_modal_progress_easing || 'ease-out');
      setFadeInDuration(data.geo_modal_fade_in_duration || '300');
      setStepAnimation(data.geo_modal_step_animation || 'slide-up');
      setSuccessAnimation(data.geo_modal_success_animation || 'scale-bounce');
      try {
        const map = JSON.parse(data.geo_modal_country_locale_map || '{}');
        setCountryMap(Object.entries(map).map(([code, locale]) => ({ code, locale: locale as string })));
      } catch { setCountryMap([]); }
      setDataLoaded(Date.now());
    } catch (e: any) {
      console.error('[GeoModal Admin] Erro ao carregar:', e?.message || e);
      setLoadError(e?.message || 'Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setSaved(false);
    try {
      const mapObj: Record<string, string> = {};
      countryMap.forEach(m => { if (m.code) mapObj[m.code.toUpperCase()] = m.locale; });

      await api.put('/admin/geo-modal/settings', {
        geo_modal_enabled: enabled ? 'true' : 'false',
        geo_modal_steps: JSON.stringify(steps),
        geo_modal_success_text: successText,
        geo_modal_scarcity_text: scarcityText,
        geo_modal_scarcity_slots: scarcitySlots,
        geo_modal_redirect_delay: redirectDelay,
        geo_modal_cta_text: ctaText,
        geo_modal_skip_text: skipText,
        geo_modal_cookie_days: cookieDays,
        geo_modal_fallback_locale: fallbackLocale,
        geo_modal_transition_delay: transitionDelay,
        geo_modal_success_delay: successDelay,
        geo_modal_progress_easing: progressEasing,
        geo_modal_fade_in_duration: fadeInDuration,
        geo_modal_step_animation: stepAnimation,
        geo_modal_success_animation: successAnimation,
        geo_modal_country_locale_map: JSON.stringify(mapObj),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      console.error('Erro ao salvar:', e);
      alert('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  }

  function updateStep(idx: number, field: keyof Step, value: any) {
    const next = [...steps];
    (next[idx] as any)[field] = field === 'duration' ? parseInt(value) || 1000 : value;
    setSteps(next);
  }

  function moveStep(idx: number, dir: -1 | 1) {
    const next = [...steps];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setSteps(next);
  }

  if (loading) {
    return <div style={{ padding: 40, color: T.textSecondary, textAlign: 'center' }}>Carregando...</div>;
  }

  return (
    <div key={dataLoaded} style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
      {/* Error banner */}
      {loadError && (
        <div style={{ padding: '12px 16px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, marginBottom: 16, color: '#ef4444', fontSize: 13 }}>
          Erro ao carregar configurações: {loadError}
        </div>
      )}
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
        <h1 style={{ color: T.text, fontSize: 22, fontWeight: 700, margin: 0 }}>
          🌍 Geo Modal — Verificação Regional
        </h1>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <span style={{ color: T.textSecondary, fontSize: 13 }}>{enabled ? 'Ativo' : 'Desativado'}</span>
          <div
            onClick={() => setEnabled(!enabled)}
            style={{
              width: 44, height: 24, borderRadius: 12, cursor: 'pointer',
              background: enabled ? '#2F6BFF' : T.border,
              position: 'relative', transition: 'background 0.2s',
            }}
          >
            <div style={{
              width: 20, height: 20, borderRadius: 10, background: '#fff',
              position: 'absolute', top: 2,
              left: enabled ? 22 : 2,
              transition: 'left 0.2s',
            }} />
          </div>
        </label>
      </div>

      {/* Etapas da Verificação */}
      <div style={sectionStyle}>
        <h3 style={{ color: T.text, fontSize: 16, fontWeight: 600, margin: '0 0 16px' }}>📋 Etapas da Verificação</h3>
        {steps.map((step, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
            <input
              value={step.icon}
              onInput={(e: any) => updateStep(i, 'icon', e.target.value)}
              style={{ ...inputStyle, width: 50, textAlign: 'center', fontSize: 18 }}
              placeholder="🔍"
            />
            <input
              value={step.text}
              onInput={(e: any) => updateStep(i, 'text', e.target.value)}
              style={{ ...inputStyle, flex: 1 }}
              placeholder="Texto da etapa..."
            />
            <input
              type="number"
              value={step.duration}
              onInput={(e: any) => updateStep(i, 'duration', e.target.value)}
              style={{ ...inputStyle, width: 80, textAlign: 'center' }}
              placeholder="ms"
            />
            <button onClick={() => moveStep(i, -1)} style={btnSmall} title="Mover para cima">↑</button>
            <button onClick={() => moveStep(i, 1)} style={btnSmall} title="Mover para baixo">↓</button>
            <button
              onClick={() => setSteps(steps.filter((_, j) => j !== i))}
              style={{ ...btnSmall, color: '#ef4444' }}
              title="Remover"
            >🗑</button>
          </div>
        ))}
        <button
          onClick={() => setSteps([...steps, { icon: '✨', text: 'Nova etapa...', duration: 1000 }])}
          style={{ ...btnSmall, marginTop: 8 }}
        >+ Adicionar etapa</button>
      </div>

      {/* Mensagem de Sucesso */}
      <div style={sectionStyle}>
        <h3 style={{ color: T.text, fontSize: 16, fontWeight: 600, margin: '0 0 16px' }}>✅ Mensagem de Sucesso</h3>
        <label style={labelStyle}>Texto de sucesso <span style={{ opacity: 0.5 }}>(placeholders: {'{city}'}, {'{country}'}, {'{flag}'})</span></label>
        <input value={successText} onInput={(e: any) => setSuccessText(e.target.value)} style={{ ...inputStyle, marginBottom: 12 }} />
        <label style={labelStyle}>Texto de escassez <span style={{ opacity: 0.5 }}>(placeholder: {'{slots}'})</span></label>
        <input value={scarcityText} onInput={(e: any) => setScarcityText(e.target.value)} style={{ ...inputStyle, marginBottom: 12 }} />
        <label style={labelStyle}>Número de vagas</label>
        <input type="number" value={scarcitySlots} onInput={(e: any) => setScarcitySlots(e.target.value)} style={{ ...inputStyle, width: 120 }} />
      </div>

      {/* Timing & Conversão */}
      <div style={sectionStyle}>
        <h3 style={{ color: T.text, fontSize: 16, fontWeight: 600, margin: '0 0 8px' }}>⏱️ Timing & Conversão</h3>
        <p style={{ color: T.textSecondary, fontSize: 12, margin: '0 0 16px', lineHeight: 1.5 }}>
          Baseado em pesquisa UX (Labor Illusion, Harvard 2011): usuários confiam mais quando veem "trabalho" sendo feito. 
          Total ideal: 6-10s. Muito rápido (&lt;3s) parece fake. Muito lento (&gt;15s) causa abandono.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>Pausa entre etapas (ms)</label>
            <input type="number" value={transitionDelay} onInput={(e: any) => setTransitionDelay(e.target.value)} style={inputStyle} />
            <span style={{ color: T.textSecondary, fontSize: 11 }}>Recomendado: 400-800ms</span>
          </div>
          <div>
            <label style={labelStyle}>Delay antes do sucesso (ms)</label>
            <input type="number" value={successDelay} onInput={(e: any) => setSuccessDelay(e.target.value)} style={inputStyle} />
            <span style={{ color: T.textSecondary, fontSize: 11 }}>Recomendado: 600-1200ms</span>
          </div>
          <div>
            <label style={labelStyle}>Duração fade-in modal (ms)</label>
            <input type="number" value={fadeInDuration} onInput={(e: any) => setFadeInDuration(e.target.value)} style={inputStyle} />
            <span style={{ color: T.textSecondary, fontSize: 11 }}>Recomendado: 200-400ms</span>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>Animação das etapas</label>
            <select value={stepAnimation} onChange={(e: any) => setStepAnimation(e.target.value)} style={{ ...inputStyle, padding: '10px 14px' }}>
              <option value="slide-up">↑ Slide Up (recomendado)</option>
              <option value="fade">✨ Fade In</option>
              <option value="slide-left">← Slide Left</option>
              <option value="scale">🔍 Scale In</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Animação do sucesso</label>
            <select value={successAnimation} onChange={(e: any) => setSuccessAnimation(e.target.value)} style={{ ...inputStyle, padding: '10px 14px' }}>
              <option value="scale-bounce">🎉 Scale Bounce (recomendado)</option>
              <option value="fade">✨ Fade In</option>
              <option value="slide-up">↑ Slide Up</option>
              <option value="confetti">🎊 Confetti Effect</option>
            </select>
          </div>
        </div>
        <div>
          <label style={labelStyle}>Easing da barra de progresso</label>
          <select value={progressEasing} onChange={(e: any) => setProgressEasing(e.target.value)} style={{ ...inputStyle, padding: '10px 14px', maxWidth: 300 }}>
            <option value="ease-out">Ease Out — desacelera no fim (recomendado)</option>
            <option value="linear">Linear — velocidade constante</option>
            <option value="ease-in-out">Ease In/Out — suave início e fim</option>
            <option value="ease-in">Ease In — acelera gradualmente</option>
          </select>
        </div>
        {/* Cálculo do tempo total */}
        {(() => {
          const totalSteps = steps.reduce((s, st) => s + st.duration, 0);
          const totalTransitions = Math.max(0, steps.length - 1) * (parseInt(transitionDelay) || 0);
          const total = totalSteps + totalTransitions + (parseInt(successDelay) || 0) + (parseInt(fadeInDuration) || 0);
          const seconds = (total / 1000).toFixed(1);
          const quality = total >= 6000 && total <= 10000 ? '🟢 Ideal' : total < 6000 ? '🟡 Rápido demais' : '🔴 Lento demais';
          return (
            <div style={{
              marginTop: 16, padding: '12px 16px', borderRadius: 8,
              background: total >= 6000 && total <= 10000 ? 'rgba(34,197,94,0.08)' : total < 6000 ? 'rgba(245,158,11,0.08)' : 'rgba(239,68,68,0.08)',
              border: `1px solid ${total >= 6000 && total <= 10000 ? 'rgba(34,197,94,0.2)' : total < 6000 ? 'rgba(245,158,11,0.2)' : 'rgba(239,68,68,0.2)'}`,
            }}>
              <div style={{ color: T.text, fontSize: 14, fontWeight: 600 }}>
                {quality} — Tempo total: {seconds}s
              </div>
              <div style={{ color: T.textSecondary, fontSize: 12, marginTop: 4 }}>
                Etapas: {(totalSteps/1000).toFixed(1)}s + Transições: {(totalTransitions/1000).toFixed(1)}s + Sucesso: {((parseInt(successDelay)||0)/1000).toFixed(1)}s + Fade: {((parseInt(fadeInDuration)||0)/1000).toFixed(1)}s
              </div>
            </div>
          );
        })()}
      </div>

      {/* Redirect */}
      <div style={sectionStyle}>
        <h3 style={{ color: T.text, fontSize: 16, fontWeight: 600, margin: '0 0 16px' }}>🔄 Redirect</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          <div>
            <label style={labelStyle}>Delay redirect (ms)</label>
            <input type="number" value={redirectDelay} onInput={(e: any) => setRedirectDelay(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Texto botão CTA</label>
            <input value={ctaText} onInput={(e: any) => setCtaText(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Texto botão Pular</label>
            <input value={skipText} onInput={(e: any) => setSkipText(e.target.value)} style={inputStyle} />
          </div>
        </div>
      </div>

      {/* Mapeamento País → Locale */}
      <div style={sectionStyle}>
        <h3 style={{ color: T.text, fontSize: 16, fontWeight: 600, margin: '0 0 16px' }}>🗺️ Mapeamento País → Locale</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '80px 50px 1fr 120px 40px', gap: '6px 10px', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ ...labelStyle, marginBottom: 0 }}>Código</span>
          <span style={{ ...labelStyle, marginBottom: 0 }}>🏳️</span>
          <span style={{ ...labelStyle, marginBottom: 0 }}>País</span>
          <span style={{ ...labelStyle, marginBottom: 0 }}>Locale</span>
          <span />
          {countryMap.map((m, i) => (
            <>
              <input
                key={`code-${i}`}
                value={m.code}
                onInput={(e: any) => {
                  const next = [...countryMap];
                  next[i] = { ...next[i], code: e.target.value.toUpperCase().slice(0, 2) };
                  setCountryMap(next);
                }}
                style={{ ...inputStyle, textAlign: 'center', textTransform: 'uppercase' }}
                maxLength={2}
              />
              <span key={`flag-${i}`} style={{ fontSize: 20, textAlign: 'center' }}>{getFlag(m.code)}</span>
              <span key={`name-${i}`} style={{ color: T.text, fontSize: 13 }}>{COUNTRY_NAMES[m.code] || m.code}</span>
              <select
                key={`locale-${i}`}
                value={m.locale}
                onChange={(e: any) => {
                  const next = [...countryMap];
                  next[i] = { ...next[i], locale: e.target.value };
                  setCountryMap(next);
                }}
                style={{ ...inputStyle, padding: '8px 10px' }}
              >
                {LOCALES.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
              <button
                key={`del-${i}`}
                onClick={() => setCountryMap(countryMap.filter((_, j) => j !== i))}
                style={{ ...btnSmall, color: '#ef4444' }}
              >🗑</button>
            </>
          ))}
        </div>
        <button
          onClick={() => setCountryMap([...countryMap, { code: '', locale: fallbackLocale }])}
          style={{ ...btnSmall, marginTop: 4 }}
        >+ Adicionar país</button>
      </div>

      {/* Configurações */}
      <div style={sectionStyle}>
        <h3 style={{ color: T.text, fontSize: 16, fontWeight: 600, margin: '0 0 16px' }}>⚙️ Configurações</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <label style={labelStyle}>Dias do cookie</label>
            <input type="number" value={cookieDays} onInput={(e: any) => setCookieDays(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Locale fallback</label>
            <select value={fallbackLocale} onChange={(e: any) => setFallbackLocale(e.target.value)} style={{ ...inputStyle, padding: '10px 14px' }}>
              {LOCALES.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        </div>
        <button onClick={() => setShowPreview(!showPreview)} style={{ ...btnSmall, marginTop: 16 }}>
          {showPreview ? '🙈 Esconder Preview' : '👁️ Preview Modal'}
        </button>
      </div>

      {/* Preview inline */}
      {showPreview && (
        <div style={{
          ...sectionStyle,
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(16px)',
          textAlign: 'center',
          padding: '32px 24px',
        }}>
          <div style={{
            background: 'rgba(15, 20, 35, 0.98)',
            border: '1px solid rgba(47,107,255,0.15)',
            borderRadius: 20,
            padding: '32px 28px',
            maxWidth: 420,
            margin: '0 auto',
          }}>
            <div style={{ fontSize: 28, marginBottom: 16 }}>⚡ WhiteLabelClaw</div>
            {steps.map((step, i) => (
              <div key={i} style={{ color: T.text, fontSize: 15, marginBottom: 8, opacity: 0.8 }}>
                <span style={{ fontSize: 18, marginRight: 8 }}>{step.icon}</span>
                {step.text}
                <span style={{ color: T.textSecondary, fontSize: 11, marginLeft: 8 }}>({step.duration}ms)</span>
              </div>
            ))}
            <div style={{ margin: '20px 0', padding: '16px 0', borderTop: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>🇧🇷</div>
              <div style={{ color: T.text, fontSize: 16, fontWeight: 600 }}>
                {successText.replace('{city}', 'São Paulo').replace('{country}', 'Brasil').replace('{flag}', '🇧🇷')}
              </div>
              <div style={{ color: '#f59e0b', fontSize: 13, marginTop: 8 }}>
                {scarcityText.replace('{slots}', scarcitySlots)}
              </div>
            </div>
            <button style={{ ...btnPrimary, width: '100%', marginBottom: 8 }}>{ctaText}</button>
            <div style={{ color: '#64748B', fontSize: 12, cursor: 'pointer' }}>{skipText}</div>
          </div>
        </div>
      )}

      {/* Footer — Salvar */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 8 }}>
        <button onClick={handleSave} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
          {saving ? '💾 Salvando...' : '💾 Salvar Configurações'}
        </button>
        {saved && <span style={{ color: '#22c55e', fontSize: 13, fontWeight: 500 }}>✅ Salvo com sucesso!</span>}
      </div>
    </div>
  );
}
